import React, { memo } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { HistoryOutlined, LockOutlined } from '@mui/icons-material';
import { format } from 'date-fns';
import { SheetStudent } from '../../../types/examination';
import PersonCell from '../../people/PersonCell';
import { ToneChip } from '../../common/StatusChip';
import PaperInput from '../marks/PaperInput';
import { PaperDraft, draftProblem, paperText } from '../marks/paperStatus';
import type { Refused } from './useMarksSheet';

interface Props {
  index: number;
  student: SheetStudent;
  draft: PaperDraft;
  maxMarks: number;
  /** Viewing only (no permission to enter marks). */
  readOnly?: boolean;
  /** Why the server refused this paper on the last save. */
  refused?: Refused;
  onChange: (index: number, next: PaperDraft) => void;
  registerInput: (index: number, el: HTMLInputElement | null) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent) => void;
  onHistory: (student: SheetStudent) => void;
}

const day = (d: string) => format(new Date(d), 'd MMM');

/** One student on a marks sheet. */
const MarksSheetRow: React.FC<Props> = ({ index, student, draft, maxMarks, readOnly, refused, onChange, registerInput, onKeyDown, onHistory }) => {
  const theme = useTheme();
  const { paper } = student;
  const tags = (
    <Stack direction="row" spacing={0.5} component="span" sx={{ display: 'inline-flex' }}>
      {student.leftOn && <ToneChip tone="warning" label={`Left ${day(student.leftOn)}`} sx={{ height: 18, fontSize: 10 }} />}
      {student.joinedOn && <ToneChip tone="info" label={`Joined ${day(student.joinedOn)}`} sx={{ height: 18, fontSize: 10 }} />}
    </Stack>
  );

  return (
    <Box
      component="tr"
      sx={{
        bgcolor: refused ? alpha(theme.palette.error.main, 0.04) : undefined,
        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
        '&:focus-within': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        '&:hover .row-history': { opacity: 1 },
      }}
    >
      <Box component="td" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', width: 56 }}>{student.rollNumber ?? '—'}</Box>
      <Box component="td" sx={{ minWidth: 200 }}>
        <PersonCell
          name={student.name}
          photoFilename={student.photoFilename}
          secondary={student.leftOn || student.joinedOn ? tags : undefined}
        />
      </Box>
      <Box component="td">
        <PaperInput
          draft={draft}
          maxMarks={maxMarks}
          locked={paper.isLocked || readOnly}
          label={student.name}
          error={!!refused}
          onChange={next => onChange(index, next)}
          inputRef={el => registerInput(index, el)}
          onKeyDown={e => onKeyDown(index, e)}
        />
        {refused
          ? (
            <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5, maxWidth: 380 }}>
              {refused.code === 'changed'
                ? `Changed to ${paperText(paper)}${paper.enteredBy ? ` by ${paper.enteredBy}` : ''} while you had this open. Save again to replace it.`
                : `Not saved — ${refused.reason}`}
            </Typography>
          )
          : !paper.isLocked && draftProblem(draft, maxMarks) && draft.marks !== '' && (
            <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>{draftProblem(draft, maxMarks)}</Typography>
          )}
      </Box>
      <Box component="td" sx={{ width: 76, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {paper.isLocked && (
          <Tooltip title={`Submitted${paper.lockedBy ? ` by ${paper.lockedBy}` : ''}${paper.lockedAt ? ` on ${format(new Date(paper.lockedAt), 'd MMM yyyy, h:mm a')}` : ''}`}>
            <LockOutlined sx={{ fontSize: 16, color: 'text.disabled', verticalAlign: 'middle', mr: 0.5 }} />
          </Tooltip>
        )}
        <Tooltip title="History">
          <IconButton size="small" className="row-history" tabIndex={-1} onClick={() => onHistory(student)} sx={{ opacity: { xs: 1, md: 0.35 }, transition: 'opacity 120ms' }} aria-label={`History for ${student.name}`}>
            <HistoryOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default memo(MarksSheetRow);
